// A soft "plop"/"bloop" notification sound synthesized with the Web Audio API — no asset file needed.
// A sine tone with a quick upward pitch bend and a gentle envelope, rounded off with a low-pass.
// Fails silently if audio is unavailable or blocked by autoplay policy.

let ctx: AudioContext | null = null;

const getContext = (): AudioContext | null => {
  try {
    if (!ctx) {
      const Ctor = window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
      if (!Ctor) return null;
      ctx = new Ctor();
    }
    return ctx;
  } catch {
    return null;
  }
};

// Browsers block audio until the user has interacted with the page — so a sound triggered from a
// background poll stays silent. Create + resume the AudioContext on the first user gesture to unlock it;
// after that, programmatic plays (e.g. a new notification) work. Runs once when this module is imported.
let unlocked = false;
const unlockAudio = () => {
  if (unlocked) return;
  unlocked = true;
  const audio = getContext();
  if (audio && audio.state === 'suspended') {
    void audio.resume();
  }
  window.removeEventListener('pointerdown', unlockAudio);
  window.removeEventListener('keydown', unlockAudio);
  window.removeEventListener('click', unlockAudio);
};

if (typeof window !== 'undefined') {
  window.addEventListener('pointerdown', unlockAudio, { once: true });
  window.addEventListener('keydown', unlockAudio, { once: true });
  window.addEventListener('click', unlockAudio, { once: true });
}

export const playNotificationSound = (): void => {
  const audio = getContext();
  if (!audio) return;

  try {
    if (audio.state === 'suspended') {
      void audio.resume();
    }
    const now = audio.currentTime;

    // Sine with a quick upward pitch bend → the classic soft "plop/bloop".
    const osc = audio.createOscillator();
    osc.type = 'sine';
    osc.frequency.setValueAtTime(330, now);
    osc.frequency.exponentialRampToValueAtTime(760, now + 0.085);

    // Round it off so it stays soft (no bright edge).
    const lowpass = audio.createBiquadFilter();
    lowpass.type = 'lowpass';
    lowpass.frequency.value = 1500;

    // Quick attack, gentle decay.
    const gain = audio.createGain();
    gain.gain.setValueAtTime(0.0001, now);
    gain.gain.exponentialRampToValueAtTime(0.3, now + 0.012);
    gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.22);

    osc.connect(lowpass).connect(gain).connect(audio.destination);
    osc.start(now);
    osc.stop(now + 0.24);
  } catch {
    // Ignore — audio not available.
  }
};
